import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  faBone,
  faEnvelope,
  faFileExport,
  faImage,
  faNoteMedical,
  faScrollOld,
  faSync,
  faToolbox,
  faUser,
  faUsers,
} from "@fortawesome/pro-solid-svg-icons";
import { useWindowDimensions } from "@hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import AnalyticsSettings from "./AnalyticsSettings";
import {
  AutoSoapSettingsAccount,
  NoteSettings,
} from "./AutoSOAPSettings";
import EmailExportingSettings from "./EmailExportingSettings";
import FreePlanSettings from "./FreePlanSettings";
import LegacySettings from "./LegacySettings";
import PDFCustomizationSettings from "./PDFCustomizationSettings";
import PimsExportingSettings from "./PimsExportingSettings";
import PreferencesSettings from "./PreferencesSettings";
import ProfileSettings from "./ProfileSettings";
import ReferralSettings from "./ReferralSettings";
import RevisionsSettings from "./RevisionsSettings";
import SubscriptionSettings from "./SubscriptionSettings";
import TeamSettings from "./Teams/TeamSettings";
import Twemoji from "../../Twemoji";
import { getTempScribenoteFlask } from "../../utils";
import { alert } from "../common/Alert";
import FullScreenLoading from "../common/loading/FullScreenLoading";
import { DSModal, DSTabs } from "../design-system/organisms";
import PageTitle from "../layout/PageTitle";

const GET_USER_ACCOUNT_DETAILS = gql`
  query GetUserAccountDetails {
    user {
      uuid
      email
      firstName
      lastName
      isConfirmed
      createdAt
      autoExportEmail
      emailExpiryDays
      shouldEmbedEmailExports
      shouldUseAutoSoaps
      numReferredSignups
      referral {
        referralCode
      }
      teams {
        name
        uuid
        users {
          firstName
          lastName
          email
          uuid
        }
      }
      account {
        uuid
        logoFileLink
        shouldHidePresetTemplates
        headerInfoPdf
        accountType
        isSubscribed
        isTeamsCustomer
        isStudent
        freeTrialExpiresAt
        accountDetails {
          planName
          nextBillingDate
          billingFrequency
          planPrice
          isCanceled
          isCanceledFinal
          isOnFreeTrial
          quantity
        }
        autoUsageDetails {
          isActiveAutoUser
          monthlyUsageCount
          monthlyTotalPrice
        }
      }
    }
  }
`;

const VALID_TABS = [
  "referrals",
  "profile",
  "team",
  "subscription",
  "notes",
  "email",
  "pims",
  "pdf",
  "preferences",
  "legacy",
];

const MOBILE_BREAKPOINT = 1024;

export default function UserAccount() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [
    shouldShowCancelSubscriptionModal,
    setShouldShowCancelSubscriptionModal,
  ] = useState(false);
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const { releaseWebPayItFurwardReferrals } = useFlags();

  const tabParam = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(tabParam)
    ? tabParam
    : releaseWebPayItFurwardReferrals
      ? "referrals"
      : "profile";

  const setActiveTab = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const { loading, data, refetch } = useQuery(
    GET_USER_ACCOUNT_DETAILS,
  );

  const { control, setValue } = useForm();

  if (!loading && !data) {
    return null;
  }

  const { user } = data || {};

  const SCRIBENOTE_FLASK = getTempScribenoteFlask();

  function hideModal() {
    setShouldShowCancelSubscriptionModal(false);
  }
  return (
    <>
      <FullScreenLoading loadingTrigger={loading} />
      <PageTitle title="Account" />

      <DSModal
        isOpen={shouldShowCancelSubscriptionModal}
        onClose={hideModal}
        title="Cancel Subscription"
        dataTestId="cancel-subscription-modal"
        actions={[
          {
            label: "Never Mind",
            variant: "cancel",
            onClick: hideModal,
          },
          {
            label: "Yes, Cancel",
            variant: "primary",
            onClick: async () => {
              await fetch(`${SCRIBENOTE_FLASK}/cancel-subscription`, {
                method: "post",
                headers: {
                  "Content-Type": "application/json",
                },
                body: {},
                credentials: "include",
              });

              mixpanel.track("Subscription Canceled");
              alert("success", "Subscription canceled successfully.");

              setTimeout(function () {
                window.location.replace(
                  "https://scribenote.typeform.com/to/vSrMU2ZC",
                );
              }, 3500);
            },
          },
        ]}
      >
        <div className="space-y-md">
          <h5 className="text-sm font-bold text-primary">
            This will have the following effects:
          </h5>
          <div className="flex flex-col items-center text-secondary">
            <ul className="list-disc text-sm">
              <li>
                - Your plan will remain active until the end of the
                billing cycle.
              </li>
              <li>- You will be downgraded to the free plan.</li>
              <li>
                - You will no longer be able to create unlimited
                Medical Records.
              </li>
              <li>
                - All of your existing notes will be maintained.
              </li>
            </ul>
          </div>

          <h4 className="py-md text-primary">
            Are you sure you&apos;d like to{" "}
            <strong>cancel your subscription</strong>? We&apos;d be
            paw-fully sad to see you go <Twemoji emoji="😿" />
          </h4>
        </div>
      </DSModal>
      <div id="accountOverview" className="mt-lg">
        <DSTabs
          value={activeTab}
          onChange={setActiveTab}
          orientation={isMobile ? "horizontal" : "vertical"}
          dataTestId="account-settings-tabs"
        >
          <DSTabs.List>
            {releaseWebPayItFurwardReferrals && (
              <DSTabs.Tab value="referrals" icon={faBone}>
                Pay It Furward
              </DSTabs.Tab>
            )}
            <DSTabs.Tab value="profile" icon={faUser}>
              Profile
            </DSTabs.Tab>
            <DSTabs.Tab value="team" icon={faUsers}>
              Team
            </DSTabs.Tab>
            <DSTabs.Tab value="subscription" icon={faSync}>
              Subscription
            </DSTabs.Tab>
            <DSTabs.Tab value="notes" icon={faNoteMedical}>
              Notes
            </DSTabs.Tab>
            <DSTabs.Tab value="email" icon={faEnvelope}>
              Email Exporting
            </DSTabs.Tab>
            <DSTabs.Tab value="pims" icon={faFileExport}>
              PIMS Exporting
            </DSTabs.Tab>
            <DSTabs.Tab value="pdf" icon={faImage}>
              PDF Customization
            </DSTabs.Tab>
            <DSTabs.Tab value="preferences" icon={faToolbox}>
              Preferences
            </DSTabs.Tab>
            <DSTabs.Tab value="legacy" icon={faScrollOld}>
              Legacy Features
            </DSTabs.Tab>
          </DSTabs.List>

          {releaseWebPayItFurwardReferrals && (
            <DSTabs.Panel value="referrals">
              <ReferralSettings />
            </DSTabs.Panel>
          )}

          <DSTabs.Panel value="profile">
            <ProfileSettings />
          </DSTabs.Panel>

          <DSTabs.Panel value="team">
            <TeamSettings />
          </DSTabs.Panel>

          <DSTabs.Panel value="subscription">
            {/* don't allow students in this whole block, needs refactor */}
            {user?.account?.isSubscribed &&
            !user?.account?.accountDetails?.isCanceledFinal ? (
              <SubscriptionSettings
                user={user}
                setShouldShowCancelSubscriptionModal={
                  setShouldShowCancelSubscriptionModal
                }
              />
            ) : (
              <FreePlanSettings user={user} />
            )}
          </DSTabs.Panel>

          <DSTabs.Panel value="notes">
            <NoteSettings />
          </DSTabs.Panel>

          <DSTabs.Panel value="email">
            <EmailExportingSettings
              control={control}
              setValue={setValue}
              data={data}
              refetch={refetch}
            />
          </DSTabs.Panel>

          <DSTabs.Panel value="pims">
            <PimsExportingSettings />
          </DSTabs.Panel>

          <DSTabs.Panel value="pdf">
            <PDFCustomizationSettings
              data={data}
              refetch={refetch}
              loading={loading}
            />
          </DSTabs.Panel>

          <DSTabs.Panel value="preferences">
            <PreferencesSettings />
          </DSTabs.Panel>

          <DSTabs.Panel value="legacy">
            <LegacySettings />
            <AutoSoapSettingsAccount
              accountType={user?.account?.accountType}
              shouldUse={user?.shouldUseAutoSoaps}
            />
            <RevisionsSettings />
            <AnalyticsSettings />
          </DSTabs.Panel>
        </DSTabs>
      </div>
    </>
  );
}
